import React, { useRef, useState, useLayoutEffect } from 'react';
import { Divider } from '@mui/material';
import { ReactComponent as ClientPoolSvg } from 'assets/svg/Main/client-pool.svg';
import { ReactComponent as CompetitorChartSvg } from 'assets/svg/Main/TopTab-Competitor-Chart.svg';
import { ReactComponent as OverviewSvg } from 'assets/svg/Main/TopTab-Overview.svg';
import { ReactComponent as SaveListingSvg } from 'assets/svg/Main/TopTab-SaveListing-Heart.svg';
import { ReactComponent as AISearchSvg } from 'assets/svg/AI-Search-Icon.svg';
import { ReactComponent as CatastroSearchSvg } from 'assets/svg/CatastroSearch-Icon.svg';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useUserStore from 'store/useUserStore';
import { useUtilsStore } from 'store/useUtilsStore';
import styled from 'styled-components';
import { ROUTES } from 'util/Constants';

const TopTab = styled.div`
    display: flex;
    cursor: pointer;
    gap: 10px;
    align-items: center;
    z-index: 1; /* keep above animated background */
`;

const TabsWrapper = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    height: 48px; /* consistent height for computing */
`;

const TopTabs: React.FC = () => {
    const { userInfo } = useUserStore();
    const onboardingDidNotFinish = !userInfo?.accountType || !userInfo?.subscriptionId;
    if (onboardingDidNotFinish) return null;

    return (
        <>
            <div className="rounded-[8px] bg-my-gray bg-opacity-40 text-[12px] font-medium leading-[24px] p-[4px] h-[48px] gap-[10px] items-center flex xs:hidden">
                <CustomTopTab isSmallScreen={false} />
            </div>
            <div className="xs:block hidden my-1">
                <Divider />
                <div className="flex gap-4 flex-col justify-start items-start my-3 mx-2">
                    <CustomTopTab isSmallScreen />
                </div>
            </div>
        </>
    );
};

export default TopTabs;

interface CustomTopTabProps {
    isSmallScreen: boolean;
}

const CustomTopTab: React.FC<CustomTopTabProps> = ({ isSmallScreen }) => {
    const { topTabIndex, setTopTabIndex } = useUtilsStore();
    const { userInfo, isProUser } = useUserStore();
    const navigate = useNavigate();
    const browserPath = window.location.pathname;

    // Define all possible tab items
    const allTabItems = [
        { index: 0, label: 'Overview', icon: <OverviewSvg />, dataCy: 'overview-tab' },
        { index: 2, label: 'Saved Listings', icon: <SaveListingSvg />, dataCy: 'savelisting-tab' },
        { index: 1, label: 'Partners', icon: <CompetitorChartSvg />, dataCy: 'competitors-tab' },
        { index: 4, label: 'Buyer Pool', icon: <ClientPoolSvg />, dataCy: 'client-pool-tab' },
        { index: 6, label: 'Catastro Search', icon: <CatastroSearchSvg />, dataCy: 'catastro-search-tab' },
        // { index: 5, label: 'Beta: Advanced AI Matching', icon: <AISearchSvg />, dataCy: 'ai-matching-tab' },
    ];

    // Filter tab items based on account type and plan
    const tabItems = allTabItems.filter((item) => {
        if (item.index === 4) {
            // Buyer Pool tab - only show for Company accounts
            return userInfo?.accountType === 'business';
        }
        if (item.index === 6) {
            // Catastro Search tab - only show for Pro users
            return isProUser();
        }
        return true;
    });

    const onTopTabClicked = (index: number) => {
        if (browserPath !== ROUTES.MAIN) navigate(ROUTES.MAIN);
        setTopTabIndex(index);
    };

    // refs for each tab to measure position
    const tabRefs = useRef<Array<HTMLDivElement | null>>([]);
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, height: 0 });

    // Update indicator position when tab index changes
    useLayoutEffect(() => {
        const current = tabRefs.current[topTabIndex];
        if (current) {
            const newStyle = {
                left: current.offsetLeft,
                width: current.clientWidth,
                height: current.clientHeight,
            };
            setIndicatorStyle(newStyle);
        }
    }, [topTabIndex]); // Only depend on topTabIndex

    // vertical padding so background matches original size
    const V_PADDING = 4;

    // If userInfo is not yet loaded, show a loading state or return null
    if (!userInfo) {
        return null;
    }

    return (
        <TabsWrapper>
            {/* Animated background indicator using existing styles */}
            <motion.div
                className={isSmallScreen ? 'bg-gray-100 rounded-[6px]' : 'bg-white rounded-[6px]'}
                style={{
                    position: 'absolute',
                    top: V_PADDING,
                    left: indicatorStyle.left,
                    width: indicatorStyle.width,
                    height: indicatorStyle.height - V_PADDING * 2,
                    zIndex: 0,
                }}
                animate={{
                    left: indicatorStyle.left,
                    width: indicatorStyle.width,
                    height: indicatorStyle.height - V_PADDING * 2,
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />

            {tabItems.map(({ index, label, icon, dataCy }) => (
                <TopTab
                    key={index}
                    ref={(el) => (tabRefs.current[index] = el)}
                    className={
                        isSmallScreen
                            ? `md:px-[20px] px-[5px] py-2.5 ${browserPath === ROUTES.MAIN && topTabIndex === index ? 'w-full' : ''}`
                            : `md:px-[20px] px-[5px] py-2.5 ${browserPath === ROUTES.MAIN && topTabIndex === index ? '' : 'opacity-50'}`
                    }
                    data-cy={dataCy}
                    onClick={() => onTopTabClicked(index)}
                >
                    {icon}
                    <div className="truncate leading-[24px] text-[12px] font-medium">{label}</div>
                </TopTab>
            ))}
        </TabsWrapper>
    );
};
